// Weather-by-zone join helpers (2026-06-22).
//
// The server delivers weather as [{ zone, fetchedMs, days: [{ localDate, ... }] }]
// where localDate is a YYYY-MM-DD Central (America/Chicago) calendar date. A job
// carries `item.zone` (stamped server-side from the customer's zip) and a
// `startMs`. We join job -> weather by: weatherByZone[item.zone][centralYMD(startMs)].
//
// Degradation contract: missing zone / no weather row -> the lookup returns null
// and the caller renders no chip (never an error, never "0%").

// YYYY-MM-DD in America/Chicago for an epoch-ms value (String or Number).
// en-CA renders ISO-shaped dates (YYYY-MM-DD), so no manual reassembly.
export const centralYMD = (ms) => {
  const n = typeof ms === "string" ? parseInt(ms, 10) : ms;
  if (n == null || Number.isNaN(n)) return null;
  return new Date(n).toLocaleDateString("en-CA", {
    timeZone: "America/Chicago",
  });
};

/**
 * Build a fast lookup from the getZoneWeatherForRange result:
 *   { byZone: { [zone]: { [localDate]: dayObj } }, fetchedByZone: { [zone]: fetchedMs } }
 * Returns empty maps for null/empty input.
 */
export const buildWeatherLookup = (zoneWeather) => {
  const byZone = {};
  const fetchedByZone = {};
  if (!Array.isArray(zoneWeather)) return { byZone, fetchedByZone };
  for (const zw of zoneWeather) {
    if (!zw || !zw.zone) continue;
    const dayMap = {};
    for (const d of zw.days || []) {
      if (d && d.localDate) dayMap[d.localDate] = d;
    }
    byZone[zw.zone] = dayMap;
    fetchedByZone[zw.zone] = zw.fetchedMs || null;
  }
  return { byZone, fetchedByZone };
};

/**
 * Resolve a single { day, fetchedMs } for a job given the lookup, the job's
 * zone, and its startMs. Returns { day: null, fetchedMs: null } when anything
 * is missing so the caller can short-circuit the chip render.
 */
export const weatherForJob = (lookup, zone, startMs) => {
  if (!lookup || !zone) return { day: null, fetchedMs: null };
  const ymd = centralYMD(startMs);
  const dayMap = lookup.byZone[zone];
  if (!ymd || !dayMap) return { day: null, fetchedMs: null };
  return {
    day: dayMap[ymd] || null,
    fetchedMs: lookup.fetchedByZone[zone] || null,
  };
};
